import { useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import _ from 'lodash';

import { ALL_OPTION_KEY, RAW_DATE_FORMAT } from '@/common/constants';
import { API_URL } from '@/dtos';

import { useBaseReportTableQuery } from '../../queries';
import { PlanOmnibusBalanceReportTableParams } from '../PlanOmnibusBalanceReport.types';

export const QUERY_KEY = 'PLAN_OMNIBUS_BALANCE_REPORT_QUERY';

const MIN_SEARCH_LEN = 3;
const usePayrollContributionReportQuery = (params: Partial<PlanOmnibusBalanceReportTableParams>) => {
  const formattedStartDate = useMemo(() => {
    if (!params.startDate) {
      return null;
    }
    const parsedDate = dayjs(params.startDate);
    if (!parsedDate.isValid()) {
      return null;
    }
    return parsedDate.format(RAW_DATE_FORMAT);
  }, [params.startDate]);

  const formattedEndDate = useMemo(() => {
    if (!params.endDate) {
      return null;
    }
    const parsedDate = dayjs(params.endDate);
    if (!parsedDate.isValid()) {
      return null;
    }
    return parsedDate.format(RAW_DATE_FORMAT);
  }, [params.endDate]);

  const contributionStartDate = useMemo(() => {
    return params.isContributionMode ? formattedStartDate : null;
  }, [formattedStartDate, params.isContributionMode]);

  const contributionEndDate = useMemo(() => {
    return params.isContributionMode ? formattedEndDate : null;
  }, [formattedEndDate, params.isContributionMode]);

  const transactionStartDate = useMemo(() => {
    return params.isContributionMode ? null : formattedStartDate;
  }, [formattedStartDate, params.isContributionMode]);
  const transactionEndDate = useMemo(() => {
    return params.isContributionMode ? null : formattedEndDate;
  }, [formattedEndDate, params.isContributionMode]);

  const currentSearch = useMemo(() => {
    if (!!params?.searchString && _.trim(params?.searchString)?.length >= MIN_SEARCH_LEN) {
      return _.trim(params.searchString);
    }
    return '';
  }, [params.searchString]);
  const queries = useMemo(() => {
    return [
      QUERY_KEY,
      contributionStartDate,
      contributionEndDate,
      transactionEndDate,
      transactionStartDate,
      params?.reportPlanIds?.join(','),
      currentSearch,
      params.employerId,
    ];
  }, [
    contributionEndDate,
    contributionStartDate,
    currentSearch,
    params.employerId,
    params?.reportPlanIds,
    transactionEndDate,
    transactionStartDate,
  ]);
  const base = useBaseReportTableQuery(
    API_URL.REPORTS_PLAN_OMNIBUS_BALANCE_REPORT_BREAKDOWN,
    queries,
    {
      employer_id: params.employerId,
      show_ssn: true,
      isSSNShowed: params.isSSNShowed,
      contribution_start_date: contributionStartDate,
      contribution_end_date: contributionEndDate,
      transaction_start_date: transactionStartDate,
      transaction_end_date: transactionEndDate,
      search_value: currentSearch,
      plan_ids:
        params?.reportPlanIds && !params?.reportPlanIds.includes(ALL_OPTION_KEY)
          ? params?.reportPlanIds.join(',')
          : undefined,
    },
    {
      disabled: !params.reportPlanIds?.length,
      isPreviewMode: params.isPreviewMode,
    }
  );

  useEffect(() => {
    base.onChangePlanOptions([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.employerId]);

  useEffect(() => {
    base.setPage(0);
    base.setRawContentList([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    params.employerId,
    params?.startDate,
    params?.endDate,
    params?.reportPlanIds,
    currentSearch,
    params.employerId,
    params?.isContributionMode,
  ]);
  return base;
};

export default usePayrollContributionReportQuery;
